import React, { useCallback, useEffect, useState } from "react";
import { fetchChatReports, deleteChatReport, deleteAllChatReports } from "../services/api";
import { getUserRole, getUserPermissions } from "../services/authService";

const PER_PAGE = 10;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "N/A");

const ChatReports = () => {
  const canDelete = getUserRole() === "admin" || getUserPermissions()?.chat_reports?.delete;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchChatReports({ page, per_page: PER_PAGE });
      setData(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err.message);
      setData([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete report #${report.id} for chat "${report.chat_id}"?`)) return;
    setError("");
    try {
      await deleteChatReport(report.id);
      // Step back a page when the last row of a later page was just removed.
      if (data.length === 1 && page > 1) setPage(page - 1);
      else await loadReports();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL chat reports? They will disappear from this list.")) return;
    setError("");
    try {
      await deleteAllChatReports();
      if (page !== 1) setPage(1);
      else await loadReports();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="page-container">
      <h2 className="page-title">Chat Reports</h2>

      {canDelete && pagination.total_items > 0 && (
        <div className="filter-form">
          <button type="button" className="rate-btn rate-btn-danger" onClick={handleDeleteAll}>
            Delete all reports
          </button>
        </div>
      )}

      {error && <div style={{ color: "#dc2626", marginBottom: 12 }}>{error}</div>}

      <div className="table-container">
        <table className="call-logs-table">
          <thead>
            <tr className="table-header-row">
              <th className="table-header">#</th>
              <th className="table-header">CHAT ID</th>
              <th className="table-header">REASON</th>
              <th className="table-header">REPORTER USER ID</th>
              <th className="table-header">REPORTER NUMBER</th>
              <th className="table-header">REPORTED AT</th>
              {canDelete && <th className="table-header">ACTIONS</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading-cell">Loading...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">No reports found</td></tr>
            ) : (
              data.map((report) => (
                <tr key={report.id} className="table-row">
                  <td className="table-cell">{report.id}</td>
                  <td className="table-cell">{report.chat_id}</td>
                  <td className="table-cell">{report.reason || "N/A"}</td>
                  <td className="table-cell">{report.reporter_user_id ?? "N/A"}</td>
                  <td className="table-cell">{report.reporter_real_number || "N/A"}</td>
                  <td className="table-cell">{formatDateTime(report.created_at)}</td>
                  {canDelete && (
                    <td className="table-cell">
                      <button type="button" className="rate-btn rate-btn-danger" onClick={() => handleDelete(report)}>
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_items > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((pagination.current_page - 1) * PER_PAGE) + 1}–{Math.min(pagination.current_page * PER_PAGE, pagination.total_items)} of {pagination.total_items}{" "}
            reports (Page {pagination.current_page} of {pagination.total_pages})
          </div>
          <div className="pagination-buttons">
            <button
              onClick={() => setPage(pagination.current_page - 1)}
              disabled={!pagination.has_prev}
              className={`pagination-btn ${!pagination.has_prev ? "disabled" : ""}`}
            >
              Previous
            </button>
            <button
              onClick={() => setPage(pagination.current_page + 1)}
              disabled={!pagination.has_next}
              className={`pagination-btn ${!pagination.has_next ? "disabled" : ""}`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatReports;
